import * as m from '@staiter/i18n';
import { setLanguageTag } from '@staiter/i18n';
import type { Locale } from '@staiter/shared';
import { LOCALE_BCP47, SUPPORTED_LOCALES } from '@staiter/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Fragment, type ReactNode, useEffect, useMemo, useState } from 'react';
import { parseJwtLocale } from '~/lib/auth/jwt';
import { LocaleCtx } from './locale-context';

const STORAGE_KEY = 'staiter.locale';

function detectInitialLocale(): Locale {
  // 1. JWT claim (если логин уже был).
  const jwt = parseJwtLocale();
  if (jwt) return jwt;
  // 2. Saved local preference (смена locale до logout).
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && (SUPPORTED_LOCALES as readonly string[]).includes(saved)) {
      return saved as Locale;
    }
  } catch {
    // ignore
  }
  // 3. navigator.language (гость на /login).
  if (typeof navigator !== 'undefined') {
    const nav = navigator.language.split('-')[0];
    if (nav && (SUPPORTED_LOCALES as readonly string[]).includes(nav)) return nav as Locale;
  }
  return 'ru';
}

/**
 * F-A.4 — RuntimeLocaleProvider.
 * Проставляет `<html lang>`, вызывает `setLanguageTag`, инвалидирует queries при смене locale,
 * сохраняет preference в localStorage для next-session fallback.
 */
export function RuntimeLocaleProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [locale, setLocale] = useState<Locale>(() => detectInitialLocale());

  // Экспортируем setLocale в окно для useUpdateLocale и toggler-ов (F-A.23).
  useEffect(() => {
    (window as unknown as { __setStaiterLocale?: (l: Locale) => void }).__setStaiterLocale = (
      l: Locale,
    ) => setLocale(l);
    return () => {
      (window as unknown as { __setStaiterLocale?: unknown }).__setStaiterLocale = undefined;
    };
  }, []);

  // Paraglide runtime.
  useEffect(() => {
    setLanguageTag(locale);
  }, [locale]);

  // <html lang> + document.title (R-FE.1) — реактивно на смену locale.
  useEffect(() => {
    document.documentElement.lang = LOCALE_BCP47[locale];
    document.title = m.app_title_default();
  }, [locale]);

  // Save preference.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      // ignore
    }
  }, [locale]);

  // F-A.22 — при смене locale invalidate-им все queries (backend может
  // отдавать локализованные куски audit.tool_label / empty states).
  // biome-ignore lint/correctness/useExhaustiveDependencies: locale — реальный trigger; qc стабилен
  useEffect(() => {
    qc.invalidateQueries().catch(() => {
      // no-op
    });
  }, [locale]);

  const ctx = useMemo(() => locale, [locale]);
  // Приёмка i18n-суипа 2026-07-11 — paraglide `m.*()` вне React-состояния:
  // optimistic-смена локали (useUpdateLocale onMutate) не перерисовывала
  // статические подписи (invalidateQueries обновлял только data-компоненты).
  // Remount поддерева по key — канонический paraglide-паттерн; сброс
  // эфемерного UI-состояния приемлем для редкой операции смены языка.
  return (
    <LocaleCtx.Provider value={ctx}>
      <Fragment key={locale}>{children}</Fragment>
    </LocaleCtx.Provider>
  );
}

export function setStaiterLocale(locale: Locale): void {
  (window as unknown as { __setStaiterLocale?: (l: Locale) => void }).__setStaiterLocale?.(locale);
}
